/**
 * Global member-referral and share-link capture.
 *
 * Mounted once inside the router. Any Vitolier URL carrying `?ref=VTLR-…`
 * (from a share link or a scanned QR code) has that code held locally so the
 * attribution survives sign-up, email verification and onboarding. A canonical
 * link carrying `?s=VS…` records a first-party share visit instead.
 *
 * Capture is attribution only — it never signs anyone in, never changes
 * membership, permissions or approvals, and never creates a record.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { captureReferralFromSearch, readCapturedReferral } from "@/lib/referral/capture";
import { recordReferralEvent } from "@/lib/referral/tokens";
import { captureShareVisit } from "@/lib/sharing/visit-capture";
import { useAuth } from "@/contexts/AuthContext";
import { readQrSource, clearQrSource } from "@/lib/qr/capture";
import { recordQrEvent, submitPatronRequest } from "@/lib/qr/registry";
import { readPatronInvite, clearPatronInvite } from "@/lib/qr/pending-patron";

export function ReferralCapture() {
  const location = useLocation();
  const { user } = useAuth();

  // Registration attribution: once signed in, credit the latest code as the
  // registration source and the first scanned code as first touch (server
  // keeps one of each per member and refuses self-attribution).
  // Patron continuity: an invitation opened before sign-up becomes one
  // application after sign-in (server returns the existing one on repeats).
  useEffect(() => {
    if (!user) return;
    // Fast Track returns to the invitation, which submits and displays status.
    // Do not race that request with the legacy storage-only fallback.
    if (typeof user.user_metadata?.fast_track_destination === "string" && user.user_metadata.fast_track_destination.startsWith("/patron/invite/")) return;
    const code = readPatronInvite();
    if (!code) return;
    void submitPatronRequest(code).then(clearPatronInvite).catch((e) => {
      if (/no longer active|cannot/i.test(String(e?.message))) clearPatronInvite();
    });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const isNew = user.created_at && Date.now() - new Date(user.created_at).getTime() < 1000 * 60 * 60 * 48;
    const src = readQrSource();
    if (!src) {
      // Legacy member link (?ref=VTLR-…) with no QR scan: the same code lives in the
      // unified registry, so record one canonical registration. The server also gives
      // the member referral credit once, unless a referral is already recorded.
      const legacy = readCapturedReferral();
      if (isNew && legacy?.token) void recordQrEvent(legacy.token, "registration");
      return;
    }
    // Existing members who scan are not counted as new registrations.
    if (!isNew) { clearQrSource(); return; }
    void recordQrEvent(src.latest, "registration", undefined, src.first).finally(clearQrSource);
  }, [user]);

  useEffect(() => {
    // Share-link attribution: opaque token, deduplicated per browser session.
    void captureShareVisit(location.search, location.pathname);

    const captured = captureReferralFromSearch(location.search);
    if (!captured) return;
    // Only count activity for the visit that actually carried the code.
    const params = new URLSearchParams(location.search);
    if (!params.get("ref") && !params.get("referral")) return;
    void recordReferralEvent(captured.token, captured.entryMethod === "member_qr" ? "scan" : "open");
  }, [location.search, location.pathname]);

  return null;
}
